import { useMemo } from 'react';
import { calcPnl } from '../lib/positionHelpers';
import { usePositions } from './usePositions';
import { useProductPricesSub } from './useProductPricesSub';
import { useActiveSelectedSubaccount } from './useSelectedSubaccount';

/**
 * Calculates total unrealized PnL across all positions for the selected
 * subaccount, by adding up PnL from each position. Will likely be replaced with
 * PnL data from backend.
 */
export const useAccountUPnl = () => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const {
    data: positions,
    error: positionsError,
    isPending: positionsPending,
  } = usePositions({ subaccountId: selectedSubaccount?.id });

  // Get prices for all position products
  const { data: pricesByProductId, isPending: pricesPending } = useProductPricesSub();

  const isPending = positionsPending || pricesPending;
  const error = positionsError;
  const positionWithPrices = useMemo(
    () =>
      positions?.map((position) => ({
        ...position,
        oraclePrice: pricesByProductId[position.productId]?.oraclePrice,
      })),
    [positions, pricesByProductId]
  );
  const data = useMemo(() => {
    // Sum up PnL across all positions
    return positionWithPrices?.reduce((total, position) => {
      const oraclePrice = position.oraclePrice;
      if (!oraclePrice) {
        return total;
      }

      const positionPnl = calcPnl(oraclePrice, position.avgPrice, position.size);
      return total + positionPnl;
    }, 0n);
  }, [positionWithPrices]);

  return {
    isPending,
    error,
    data,
  };
};
